import { FC, useRef } from 'react';
import { ScheduledCallDetailsModal } from '../ScheduledCallDetailsModal/ScheduledCallDetailsModal';
import { DateRangePickerModal } from '../DateRangePickerModal/DateRangePickerModal';
import { CalendarHeader } from './CalendarHeader';
import { CalendarGrid } from './CalendarGrid';
import { useWeeklyCalendar } from './useWeeklyCalendar';
import type { UserAvailabilitySlot } from '@teachur/http-client';

interface WeeklyAvailabilityCalendarProps {
  availabilitySlots: UserAvailabilitySlot[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

export const WeeklyAvailabilityCalendar: FC<WeeklyAvailabilityCalendarProps> = ({
  availabilitySlots,
  selectedDate,
  onDateChange,
}) => {
  const calendarRef = useRef<HTMLDivElement>(null);

  const {
    selectedScheduledCall,
    setSelectedScheduledCall,
    isDateRangeModalOpen,
    setIsDateRangeModalOpen,
    createBulkSlots,
    weekStart,
    weekEnd,
    weekDates,
    workHours,
    goToPreviousWeek,
    goToNextWeek,
    goToToday,
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,
    handleSlotClick,
    getSlotClassName,
  } = useWeeklyCalendar({ availabilitySlots, selectedDate, onDateChange });

  return (
    <div className="bg-(--neutral-white-NW1000) rounded-xl! border border-neutral-200">
      <div className="px-6 py-4 border-b border-neutral-200">
        <CalendarHeader
          weekStart={weekStart}
          weekEnd={weekEnd}
          onPreviousWeek={goToPreviousWeek}
          onNextWeek={goToNextWeek}
          onToday={goToToday}
          onAddRange={() => setIsDateRangeModalOpen(true)}
        />
      </div>

      <CalendarGrid
        calendarRef={calendarRef}
        weekDates={weekDates}
        workHours={workHours}
        weekStart={weekStart}
        availabilitySlots={availabilitySlots}
        getSlotClassName={getSlotClassName}
        handleMouseDown={handleMouseDown}
        handleMouseEnter={handleMouseEnter}
        handleMouseUp={handleMouseUp}
        handleSlotClick={handleSlotClick}
      />

      {createBulkSlots.isPending && (
        <div className="p-4 border-t border-neutral-200 bg-blue-50">
          <p className="text-sm text-blue-700">Creating availability slots...</p>
        </div>
      )}

      {selectedScheduledCall && (
        <ScheduledCallDetailsModal
          scheduledCall={selectedScheduledCall}
          isOpen={!!selectedScheduledCall}
          onClose={() => setSelectedScheduledCall(null)}
        />
      )}

      <DateRangePickerModal
        isOpen={isDateRangeModalOpen}
        onClose={() => setIsDateRangeModalOpen(false)}
      />
    </div>
  );
};
